/**
 * useBoard Hook
 *
 * Board interaction logic hook. Manages:
 * - Selected square state
 * - Possible moves highlighting
 * - Square click handlers (phase-aware)
 * - Move validation
 * - Island placement validation
 *
 * Used by:
 * - Board component (rendering and interactions)
 * - BoardSquare component (click handling)
 * - IslandPlacementPhase (island placement)
 * - MonsterPlacementPhase (monster placement)
 * - ActivePhase (monster movement)
 *
 * Integration:
 * - Depends on useGameStateContext for phase, player letter, board state
 * - Depends on useMonsters for monster information
 * - Uses boardHelpers for validation and move calculation
 *
 * Dependencies: types/board.types.ts, boardHelpers.ts, GameStateContext, useMonsters.ts
 */

import { useState, useEffect, useCallback } from 'react';
import { useGameStateContext } from '../context/useGameStateContext';
import useMonsters from './useMonsters';
import { calculatePossibleMoves, isValidMonsterMove } from '../utils/monsterHelpers';
import { validateIslandPlacement, isInPlayerHalf, isValidPosition } from '../utils/boardHelpers';
import type { Position } from '../types/game.types';
import type { SquareData, IslandPlacement } from '../types/board.types';
import type { MonsterTypeId } from '../types/monster.types';

export interface UseBoardReturn {
    // Selected state
    selectedSquare: SquareData | null;
    setSelectedSquare: (square: SquareData | null) => void;
    clearSelectedSquare: () => void;

    // Possible moves
    possibleMoves: Position[];
    setPossibleMoves: (moves: Position[]) => void;
    clearPossibleMoves: () => void;

    // Island placement
    selectedIslands: IslandPlacement[];
    addIsland: (position: Position) => void;
    removeIsland: (position: Position) => void;
    clearIslands: () => void;
    validateIslandPlacement: () => { valid: boolean; error?: string };

    // Square click handlers (phase-aware)
    handleSquareClick: (square: SquareData) => void;
    handleIslandPlacementClick: (square: SquareData) => void;
    handleMonsterPlacementClick: (square: SquareData) => void;
    handleMonsterMoveClick: (square: SquareData) => void;

    // Move validation
    isValidMove: (from: Position, to: Position, monsterType: MonsterTypeId) => boolean;
    canPlaceMonster: (position: Position, monsterType: MonsterTypeId) => boolean;
    canPlaceIsland: (position: Position) => boolean;

    // Helper functions
    getSquareData: (row: number, col: number) => SquareData | null;
    isSquareSelected: (row: number, col: number) => boolean;
    isSquarePossibleMove: (row: number, col: number) => boolean;
}

/**
 * useBoard hook
 * @returns Board interaction state and functions
 */
export const useBoard = (): UseBoardReturn => {
    const { phase, myPlayerLetter, board, isMyTurn } = useGameStateContext();
    const {
        selectedMonster,
        selectedPiece,
        setSelectedPiece,
        getPiece,
    } = useMonsters();

    // Selected square
    const [selectedSquare, setSelectedSquareState] = useState<SquareData | null>(null);

    // Possible moves
    const [possibleMoves, setPossibleMovesState] = useState<Position[]>([]);

    // Island placement
    const [selectedIslands, setSelectedIslands] = useState<IslandPlacement[]>([]);

    // Clear selection when phase changes
    useEffect(() => {
        setSelectedSquareState(null);
        setPossibleMovesState([]);
        if (phase !== 'ISLAND_PLACEMENT') {
            setSelectedIslands([]);
        }
    }, [phase]);

    // Placement: preview moves from chosen type + target square.
    useEffect(() => {
        if (phase !== 'MONSTER_PLACEMENT') return;
        if (selectedMonster && selectedSquare) {
            const moves = calculatePossibleMoves(
                selectedMonster.type,
                { row: selectedSquare.row, col: selectedSquare.col },
                myPlayerLetter || 'A'
            );
            setPossibleMovesState(moves);
        } else {
            setPossibleMovesState([]);
        }
        return;
    }, [phase, selectedMonster, selectedSquare, myPlayerLetter]);

    // Update possible moves when selected piece changes on your turn
    useEffect(() => {
        if (phase !== 'ACTIVE') return;
        if (selectedPiece && selectedPiece.player === myPlayerLetter && isMyTurn) {
            const moves = calculatePossibleMoves(
                selectedPiece.type,
                selectedPiece.position,
                myPlayerLetter
            );
            setPossibleMovesState(moves);
        } else {
            setPossibleMovesState([]);
        }
    }, [selectedPiece, myPlayerLetter, isMyTurn]);

    // Selected square management
    const setSelectedSquare = useCallback((square: SquareData | null) => {
        setSelectedSquareState(square);
    }, []);

    const clearSelectedSquare = useCallback(() => {
        setSelectedSquareState(null);
        setPossibleMovesState([]);
    }, []);

    // Possible moves management
    const setPossibleMoves = useCallback((moves: Position[]) => {
        setPossibleMovesState(moves);
    }, []);

    const clearPossibleMoves = useCallback(() => {
        setPossibleMovesState([]);
    }, []);

    const isSquarePossibleMove = useCallback(
        (row: number, col: number): boolean => {
            return possibleMoves.some(m => m.row === row && m.col === col);
        },
        [possibleMoves]
    );

    // Island placement
    const addIsland = useCallback(
        (position: Position) => {
            if (!myPlayerLetter) return;

            // Check if already selected
            if (selectedIslands.find(i => i.row === position.row && i.col === position.col)) {
                return;
            }

            // Check if valid position
            if (!isValidPosition(position)) return;
            if (!canPlaceIsland(position)) return;

            // Check if we've reached the limit
            if (selectedIslands.length >= 5) return;

            setSelectedIslands(prev => [...prev, position]);
        },
        [selectedIslands, myPlayerLetter]
    );

    const removeIsland = useCallback((position: Position) => {
        setSelectedIslands(prev =>
            prev.filter(i => !(i.row === position.row && i.col === position.col))
        );
    }, []);

    const clearIslands = useCallback(() => {
        setSelectedIslands([]);
    }, []);

    const validateIslandPlacementFn = useCallback(() => {
        if (!myPlayerLetter) {
            return { valid: false, error: 'Player letter not set' };
        }
        return validateIslandPlacement(selectedIslands, myPlayerLetter);
    }, [selectedIslands, myPlayerLetter]);

    const handleIslandPlacementClick = useCallback(
        (square: SquareData) => {
            const position: Position = { row: square.row, col: square.col };
            if (!myPlayerLetter) return;
            if (!isValidPosition(position)) return;
            if (!isInPlayerHalf(position, myPlayerLetter)) return;

            // Use functional update so we always read latest state (avoids stale closure on double-click)
            setSelectedIslands((prev) => {
                const existing = prev.find(
                    (i) => i.row === position.row && i.col === position.col
                );
                if (existing) {
                    return prev.filter(
                        (i) => !(i.row === position.row && i.col === position.col)
                    );
                }
                if (prev.length >= 5) return prev;
                return [...prev, position];
            });
        },
        [myPlayerLetter]
    );

    const handleMonsterPlacementClick = useCallback(
        (square: SquareData) => {
            if (!isMyTurn) return;
            if (!selectedMonster) return;

            const position: Position = { row: square.row, col: square.col };

            if (canPlaceMonster(position, selectedMonster.id as MonsterTypeId)) {
                setSelectedSquare(square);
            }
        },
        [isMyTurn, selectedMonster, setSelectedSquare]
    );

    const handleMonsterMoveClick = useCallback(
        (square: SquareData) => {
            if (square.type === 'monster' && square.player === myPlayerLetter && square.value !== null && myPlayerLetter !== null) {
                setSelectedSquare(null);
                const piece = getPiece(square.value as MonsterTypeId, myPlayerLetter);
                if (piece) {
                    if (selectedPiece?.player === piece.player && selectedPiece?.type === piece.type) {
                        setSelectedPiece(null);
                    } else {
                        setSelectedPiece(piece);
                    }
                }
                return;
            }

            if (selectedPiece && selectedPiece.player === myPlayerLetter && isMyTurn) {
                if (isSquarePossibleMove(square.row, square.col)) {
                    setSelectedSquare(square);
                    return;
                }
                setSelectedSquare(null);
                return;
            }

            if (square.type === 'monster' && square.player) {
                const piece = getPiece(square.value as MonsterTypeId, square.player);
                if (piece) {
                    setSelectedPiece(piece);
                }
                return;
            }

            setSelectedSquare(null);
            setSelectedPiece(null);
        },
        [
            myPlayerLetter,
            isMyTurn,
            selectedPiece,
            setSelectedPiece,
            setSelectedSquare,
            getPiece,
            isSquarePossibleMove,
        ]
    );

    // Square click router (must be after phase handlers so it closes over current handlers)
    const handleSquareClick = useCallback(
        (square: SquareData) => {
            switch (phase) {
                case 'ISLAND_PLACEMENT':
                    handleIslandPlacementClick(square);
                    break;
                case 'MONSTER_PLACEMENT':
                    handleMonsterPlacementClick(square);
                    break;
                case 'ACTIVE':
                    handleMonsterMoveClick(square);
                    break;
                default:
                    break;
            }
        },
        [phase, handleIslandPlacementClick, handleMonsterPlacementClick, handleMonsterMoveClick]
    );

    // Move validation
    const isValidMoveFn = useCallback(
        (from: Position, to: Position, monsterType: MonsterTypeId): boolean => {
            if (!myPlayerLetter) return false;
            return isValidMonsterMove(monsterType, from, to, myPlayerLetter);
        },
        [myPlayerLetter]
    );

    const canPlaceMonster = useCallback(
        (position: Position, _monsterType: MonsterTypeId): boolean => {
            if (!myPlayerLetter || !board) return false;

            // Check if position is valid
            if (!isValidPosition(position)) return false;

            // Check if square is blank
            const tileKey = `${position.row}:${position.col}`;
            const tile = board.tiles[tileKey];
            if (!tile || tile.type !== 'blank') {
                return false;
            }

            // Check if position is in player's half
            return isInPlayerHalf(position, myPlayerLetter);
        },
        [myPlayerLetter, board]
    );

    const canPlaceIsland = useCallback(
        (position: Position): boolean => {
            if (!myPlayerLetter) return false;

            // Check if position is valid
            if (!isValidPosition(position)) return false;

            // Check if position is in player's half
            return isInPlayerHalf(position, myPlayerLetter);
        },
        [myPlayerLetter]
    );

    // Helper functions
    const getSquareData = useCallback(
        (row: number, col: number): SquareData | null => {
            if (!board) return null;

            const tileKey = `${row}:${col}`;
            const tile = board.tiles[tileKey];

            return {
                row,
                col,
                type: tile?.type || 'blank',
                player: tile?.player || null,
                value: tile?.value || null,
            };
        },
        [board]
    );

    const isSquareSelected = useCallback(
        (row: number, col: number): boolean => {
            return (
                selectedSquare !== null &&
                selectedSquare.row === row &&
                selectedSquare.col === col
            );
        },
        [selectedSquare]
    );

    return {
        // Selected state
        selectedSquare,
        setSelectedSquare,
        clearSelectedSquare,

        // Possible moves
        possibleMoves,
        setPossibleMoves,
        clearPossibleMoves,

        // Island placement
        selectedIslands,
        addIsland,
        removeIsland,
        clearIslands,
        validateIslandPlacement: validateIslandPlacementFn,

        // Square click handlers
        handleSquareClick,
        handleIslandPlacementClick,
        handleMonsterPlacementClick,
        handleMonsterMoveClick,

        // Move validation
        isValidMove: isValidMoveFn,
        canPlaceMonster,
        canPlaceIsland,

        // Helper functions
        getSquareData,
        isSquareSelected,
        isSquarePossibleMove,
    };
};

export default useBoard;
